/**
 * Retire du `deal` les colonnes « blob » que seul `getCGData` (route
 * /api/cg/:id/review) sélectionne — `getCGList` ne les renvoie pas.
 *
 * Pourquoi : les modales SubmitCTVO / SubmitCessionCertificate étalent le deal
 * entier dans le body (`{ ...deal, ...formData }`) qu'elles POSTent sur
 * /api/misiv/save|submit/*. Ces routes passent par le parser global
 * `bodyParser.json()` (server.ts) — limite **100 kb par défaut**.
 *
 * `ingestion_trace` est un MEDIUMTEXT qui pèse couramment plusieurs centaines
 * de Ko (489 Ko constatés sur le dossier FV-822-YY). Depuis /review le body
 * partait donc à ~490 Ko → **413 request entity too large**, avalé par le catch
 * de la modale en « Erreur lors de la sauvegarde de la demande ». Depuis
 * list-cg le même bouton fonctionnait, `getCGList` ne chargeant pas ces
 * colonnes.
 *
 * Ces deux champs ne sont consommés ni par les modales ni par MiSIV. La page
 * review, elle, lit `payload.analysisSnapshot` (champ top-level assemblé par
 * assembleReviewData), pas `registration.AnalysisSnapshot` — la purge est donc
 * sans effet sur l'affichage.
 *
 * NB : on retire une liste explicite de blobs connus plutôt que de whitelister
 * les champs utiles. `misivController` consomme un très grand nombre de champs
 * du deal (entités, adresses, co-acquéreur, VIN, formule…) : une whitelist
 * exhaustive ferait courir un risque de régression silencieuse sur list-cg,
 * qui fonctionne aujourd'hui.
 */

/** Colonnes lourdes exposées par getCGData et inutiles au submit MiSIV. */
export const DEAL_BLOB_FIELDS = ['IngestionTrace', 'AnalysisSnapshot'] as const;

/**
 * Renvoie une copie du deal sans les blobs. Sûr sur `null`/`undefined`
 * (renvoyés tels quels) et no-op si les champs sont absents (cas list-cg).
 */
export const stripDealBlobs = <T>(deal: T): T => {
  if (deal === null || typeof deal !== 'object') return deal;

  const rest = { ...(deal as Record<string, unknown>) };
  for (const field of DEAL_BLOB_FIELDS) delete rest[field];

  return rest as T;
};
